// Design Ref: §2.1 portfolioUtils.js — 순수 집계 함수 (매매일지 enriched trades 기준)
// Plan SC: FR-01, FR-02, FR-07 (monthly 탭과 합계 일치 필수)

// 기간 필터: YYYY-MM 기준 ('3M' = 최근 3개월 포함, 'YTD' = 올해, '1Y' = 최근 12개월, '1M' = 이번 달)
export function periodToRange(period, now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-index
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (yr, mo) => `${yr}-${pad(mo + 1)}`;
  const ym = fmt(y, m);
  switch (period) {
    case '1M': return { from: ym, to: ym };
    case '3M': {
      const d = new Date(y, m - 2, 1);
      return { from: fmt(d.getFullYear(), d.getMonth()), to: ym };
    }
    case 'YTD': return { from: `${y}-01`, to: ym };
    case '1Y': {
      const d = new Date(y, m - 11, 1);
      return { from: fmt(d.getFullYear(), d.getMonth()), to: ym };
    }
    default: return { from: '0000-01', to: '9999-12' };
  }
}

// 월 범위 내 enumerate: ['2026-02', '2026-03', ...]
export function monthsBetween(from, to) {
  const result = [];
  let [y, m] = from.split('-').map(Number);
  const [ey, em] = to.split('-').map(Number);
  while (y < ey || (y === ey && m <= em)) {
    result.push(`${y}-${String(m).padStart(2, '0')}`);
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return result;
}

// 월별 실현 P&L (매도 완료 건만)
// trades: enriched (isClosed, profit, sellDate 필요). US는 profitKrw 사용.
// Plan SC: monthly 탭 Stat 카드 합계와 일치해야 함
export function monthlyPnL(trades, period = 'YTD', opts = {}) {
  const { from, to } = periodToRange(period);
  const months = monthsBetween(from, to);
  const map = Object.fromEntries(months.map(m => [m, 0]));
  for (const t of trades) {
    if (!t.isClosed || !t.sellDate) continue;
    const ym = t.sellDate.slice(0, 7);
    if (!(ym in map)) continue;
    const p = opts.useKrw ? (t.profitKrw ?? t.profit ?? 0) : (t.profit ?? t.profitKrw ?? 0);
    map[ym] += Number(p) || 0;
  }
  return months.map(m => ({ month: m, value: Math.round(map[m]) }));
}

// 누적 수익률 (단순 누적 손익 / 누적 투자원금)
// 주의: 월별 원금 변동 있어 근사치. 정확한 TWR은 추후.
export function cumulativeReturn(trades, period = 'YTD', opts = {}) {
  const { from, to } = periodToRange(period);
  const months = monthsBetween(from, to);
  // 각 월 기준 누적 투자 원금 + 누적 실현 손익
  const principalBy = {};
  const pnlBy = {};
  for (const t of trades) {
    const buyYm = (t.buyDate || '').slice(0, 7);
    const sellYm = t.sellDate ? t.sellDate.slice(0, 7) : null;
    const principal = opts.useKrw ? (t.buyAmtKrw ?? t.buyAmt ?? 0) : (t.buyAmt ?? t.buyAmtKrw ?? 0);
    if (buyYm) principalBy[buyYm] = (principalBy[buyYm] || 0) + Number(principal || 0);
    if (sellYm && t.isClosed) {
      const p = opts.useKrw ? (t.profitKrw ?? t.profit ?? 0) : (t.profit ?? t.profitKrw ?? 0);
      pnlBy[sellYm] = (pnlBy[sellYm] || 0) + Number(p || 0);
    }
  }
  let cumPrincipal = 0;
  let cumPnl = 0;
  // 누적은 from 이전 월의 원금까지 포함
  Object.keys(principalBy).sort().forEach(ym => {
    if (ym < from) cumPrincipal += principalBy[ym];
  });
  return months.map(m => {
    cumPrincipal += (principalBy[m] || 0);
    cumPnl += (pnlBy[m] || 0);
    const pct = cumPrincipal > 0 ? (cumPnl / cumPrincipal) * 100 : 0;
    return { month: m, pct: Number(pct.toFixed(2)), cumPnl: Math.round(cumPnl), cumPrincipal: Math.round(cumPrincipal) };
  });
}

// 월별 승률 (매도 완료 기준 wins/total)
export function winRateByMonth(trades, period = 'YTD') {
  const { from, to } = periodToRange(period);
  const months = monthsBetween(from, to);
  const map = Object.fromEntries(months.map(m => [m, { wins: 0, losses: 0 }]));
  for (const t of trades) {
    if (!t.isClosed || !t.sellDate) continue;
    const ym = t.sellDate.slice(0, 7);
    if (!(ym in map)) continue;
    const p = t.profit ?? t.profitKrw ?? t.profitUsd ?? 0;
    if (p > 0) map[ym].wins++;
    else map[ym].losses++;
  }
  return months.map(m => ({
    month: m,
    wins: map[m].wins,
    losses: map[m].losses,
    total: map[m].wins + map[m].losses,
    rate: (map[m].wins + map[m].losses) === 0 ? null : Number(((map[m].wins / (map[m].wins + map[m].losses)) * 100).toFixed(1)),
  }));
}

// 섹터/시장 분포 (현재 보유 기준 — !isClosed)
// sector 정보가 없으면 market(KOSPI/KOSDAQ/해외) fallback
export function sectorDistribution(trades, opts = {}) {
  const palette = ['#f0c040', '#34d399', '#60a5fa', '#a78bfa', '#fb7185', '#fbbf24', '#2dd4bf', '#f472b6'];
  const buckets = {};
  for (const t of trades) {
    if (t.isClosed) continue; // 현재 보유만
    const label = t.sector || t.market || opts.fallbackLabel || '기타';
    const amt = opts.useKrw ? (t.buyAmtKrw ?? t.buyAmt ?? 0) : (t.buyAmt ?? t.buyAmtKrw ?? 0);
    buckets[label] = (buckets[label] || 0) + Number(amt || 0);
  }
  const entries = Object.entries(buckets).sort((a, b) => b[1] - a[1]);
  return entries.map(([label, value], i) => ({
    label,
    value: Math.round(value),
    color: palette[i % palette.length],
  }));
}

// Top N 승 / 패 종목 (실현 손익 기준)
export function topWinnersLosers(trades, n = 5) {
  const closed = trades.filter(t => t.isClosed);
  const getP = t => t.profit ?? t.profitKrw ?? 0;
  const sorted = [...closed].sort((a, b) => getP(b) - getP(a));
  const winners = sorted.slice(0, n).filter(t => getP(t) > 0);
  const losers = sorted.slice(-n).reverse().filter(t => getP(t) < 0);
  return { winners, losers };
}

// 전체 요약 Stat
export function overallStats(trades, opts = {}) {
  const closed = trades.filter(t => t.isClosed);
  const total = closed.length;
  const wins = closed.filter(t => (t.profit ?? t.profitKrw ?? 0) > 0).length;
  const totalPnl = closed.reduce((a, t) => a + Number(opts.useKrw ? (t.profitKrw ?? t.profit ?? 0) : (t.profit ?? t.profitKrw ?? 0)), 0);
  const totalPrincipal = closed.reduce((a, t) => a + Number(opts.useKrw ? (t.buyAmtKrw ?? t.buyAmt ?? 0) : (t.buyAmt ?? t.buyAmtKrw ?? 0)), 0);
  return {
    totalTrades: total,
    winRate: total === 0 ? null : Number(((wins / total) * 100).toFixed(1)),
    totalPnl: Math.round(totalPnl),
    avgReturn: totalPrincipal > 0 ? Number(((totalPnl / totalPrincipal) * 100).toFixed(2)) : null,
  };
}

// ES module exports 개별로 노출됨 (export function ...). 전체 default도 제공:
const portfolioUtils = {
  periodToRange,
  monthsBetween,
  monthlyPnL,
  cumulativeReturn,
  winRateByMonth,
  sectorDistribution,
  topWinnersLosers,
  overallStats,
};
export default portfolioUtils;
